<template>
  <div
    v-if="statusLabel"
    class="text-assignment-item text-assignment-item--status px-2"
  >
    <VLayout align-center row>
      <VFlex :style="columnStyles.name" class="text-xs-left">
        <div>
          <!-- Fixed width so names line up for "4." and "4-A." alike. -->
          <span
            class="row-label d-inline-block body-1 grey--text text--darken-1"
            :style="{
              minWidth: indexWidth,
              fontVariantNumeric: 'tabular-nums',
            }"
            >{{ index }}.</span
          >
          <VIcon
            v-if="!assignment.phone"
            class="no-phone-icon mr-1 mb-1"
            title="No Phone Number"
            small
            fab
            >error</VIcon
          >
          {{ assignment.name }}
          <VAvatar
            v-if="assignment.numAssgnts > 1"
            right
            size="16"
            class="num-assignments"
            >{{ assignment.numAssgnts }}</VAvatar
          >
        </div>
      </VFlex>
      <VFlex :style="columnStyles.job" class="text-xs-left body-1">{{
        assignment.jobNumber || '—'
      }}</VFlex>
      <VFlex
        :style="columnStyles.start"
        class="text-xs-left body-1 text-no-wrap"
      >
        {{ assignment.startTime || '—' }}
        <VIcon
          v-if="assignment.isNightShift"
          small
          class="night-shift night-shift-icon"
          title="Night shift"
          >brightness_3</VIcon
        >
      </VFlex>
      <VFlex
        :style="columnStyles.status"
        class="text-xs-left"
        :class="statusTextClass"
      >
        <template v-if="texting">Sending...</template>
        <template v-else-if="statusDisplay">
          <VTooltip v-if="statusWarning" top max-width="240">
            <VChip
              slot="activator"
              small
              disabled
              :color="statusDisplay.chipColor"
              :text-color="statusDisplay.textColor"
              class="status-chip ma-0"
            >
              <VIcon left small>{{ statusDisplay.icon }}</VIcon>
              {{ statusLabel }}
            </VChip>
            <span>{{ statusWarning }}</span>
          </VTooltip>
          <VChip
            v-else
            small
            disabled
            :color="statusDisplay.chipColor"
            :text-color="statusDisplay.textColor"
            class="status-chip ma-0"
          >
            <VIcon left small>{{ statusDisplay.icon }}</VIcon>
            {{ statusLabel }}
          </VChip>
        </template>
        <template v-else>{{ statusLabel }}</template>
      </VFlex>
      <!-- One fixed column per action so the icons line up under their headers. -->
      <VFlex :style="columnStyles.action" class="text-xs-center">
        <BaseButton
          v-if="clearable"
          small
          icon
          outline
          color="primary"
          md-icon="replay"
          title="Clear status"
          :loading="clearing"
          :disabled="clearing || skipping || actionsDisabled"
          @click="$emit('clear')"
        />
      </VFlex>
      <VFlex
        v-if="skippable"
        :style="columnStyles.action"
        class="text-xs-center"
      >
        <BaseButton
          small
          icon
          outline
          color="orange"
          md-icon="block"
          title="Skip"
          :loading="skipping"
          :disabled="clearing || skipping || actionsDisabled"
          @click="$emit('skip')"
        />
      </VFlex>
      <VFlex
        v-else-if="showTextColumn"
        :style="columnStyles.action"
        class="text-xs-center"
      >
        <template v-if="textable">
          <!-- A disabled button swallows hover, so the span carries the tooltip. -->
          <VTooltip v-if="!assignment.phone" top>
            <span slot="activator">
              <BaseButton small icon outline disabled md-icon="sms" />
            </span>
            <span>No phone number on file</span>
          </VTooltip>
          <VMenu
            v-else
            v-model="isConfirming"
            :close-on-content-click="false"
            :disabled="!canText"
            :max-width="300"
            attach
            offset-y
            left
          >
            <BaseButton
              slot="activator"
              small
              icon
              outline
              color="orange darken-2"
              md-icon="sms"
              title="Send text"
              :loading="texting"
              :disabled="!canText"
            />
            <VCard>
              <VCardText class="text-xs-left">
                <div class="font-weight-bold mb-1"
                  >Text {{ assignment.name }}?</div
                >
                Their shift is already in MyShifts, and laborers were told texts
                have stopped.
              </VCardText>
              <VCardActions>
                <VSpacer />
                <BaseButton
                  small
                  flat
                  color="grey darken-1"
                  @click="isConfirming = false"
                  >Cancel</BaseButton
                >
                <BaseButton small color="orange darken-2" @click="confirmText"
                  >Send text</BaseButton
                >
              </VCardActions>
            </VCard>
          </VMenu>
        </template>
      </VFlex>
    </VLayout>
    <div v-if="rowNote" :class="['caption text-xs-left mb-2', rowNoteClass]">{{
      rowNote
    }}</div>
  </div>
  <VLayout v-else class="text-assignment-item">
    <VFlex xs8 offset-xs2>
      <VLayout align-center>
        <VFlex v-if="selectable" shrink>
          <VCheckbox
            :input-value="isSelected"
            class="d-inline-block pt-0"
            hide-details
            @change="toggleSelection"
          />
        </VFlex>
        <VFlex class="text-xs-left" grow>
          <div>
            <VIcon
              v-if="!assignment.phone"
              class="no-phone-icon mr-1 mb-1"
              title="No Phone Number"
              small
              fab
              >error</VIcon
            >
            {{ index }}. {{ assignment.name }}
            <VAvatar
              v-if="assignment.numAssgnts > 1"
              right
              size="16"
              class="num-assignments"
              >{{ assignment.numAssgnts }}</VAvatar
            >
          </div>
          <div class="caption grey--text text--darken-1">
            {{ shiftSummary }}
            <span
              v-if="assignment.isNightShift"
              class="night-shift text-no-wrap"
            >
              <VIcon small class="night-shift-icon">brightness_3</VIcon> Night
            </span>
          </div>
        </VFlex>
        <VFlex
          v-if="resultLabel"
          shrink
          class="text-no-wrap font-weight-bold"
          :class="resultTextClass"
          >{{ resultLabel }}</VFlex
        >
      </VLayout>
    </VFlex>
  </VLayout>
</template>

<script>
import _get from 'lodash/get'
import VAvatar from '@vuetify/VAvatar'
import VChip from '@vuetify/VChip'
import VTooltip from '@vuetify/VTooltip'
import VMenu from '@vuetify/VMenu'
import { VCardText, VCardActions } from '@vuetify/VCard'
import { VCheckbox } from 'vuetify/lib'

export const publishResults = Object.freeze({
  PUBLISHED: 'published',
  TEXTED: 'texted',
  FAILED: 'failed',
})

// Outcome of texting one laborer from their row in the published list.
export const rowTextResults = Object.freeze({
  TEXTED: 'texted',
  FAILED: 'failed',
  FAILED_ACTIVE_CONVERSATION: 'failed_active_conversation',
  STATUS_CHANGED: 'status_changed',
  BOARD_NOT_READ: 'board_not_read',
})

// Column widths of a status row, shared with the published list's header so
// the two line up. Finer than the 12-column grid: the name gets the room and
// the action buttons only what they need.
export const statusRowColumnWidths = Object.freeze({
  NAME: 44,
  JOB: 11,
  START: 15,
  STATUS: 18,
  ACTION: 6,
})

// Status takes the last action column's share when that column is absent.
export function statusRowColumnStyles(hasLastColumn = true) {
  let widths = {
    name: statusRowColumnWidths.NAME,
    job: statusRowColumnWidths.JOB,
    start: statusRowColumnWidths.START,
    status:
      statusRowColumnWidths.STATUS +
      (hasLastColumn ? 0 : statusRowColumnWidths.ACTION),
    action: statusRowColumnWidths.ACTION,
  }
  return Object.keys(widths).reduce((styles, column) => {
    let width = `${widths[column]}%`
    return { ...styles, [column]: { flex: `0 0 ${width}`, maxWidth: width } }
  }, {})
}

const resultLabels = Object.freeze({
  [publishResults.PUBLISHED]: 'Published',
  [publishResults.TEXTED]: 'Texted',
  [publishResults.FAILED]: 'Failed',
})

export default {
  name: 'TextAssignment',
  components: {
    VAvatar,
    VChip,
    VCheckbox,
    VTooltip,
    VMenu,
    VCardText,
    VCardActions,
  },
  props: {
    // When set, the row renders in the lower "status" list with a Clear button.
    statusLabel: {
      type: String,
      default: null,
    },
    // Whether the status row also offers Skip (only SMS issue rows do).
    skippable: {
      type: Boolean,
      default: false,
    },
    // Rows still waiting on Knack for a status have nothing to clear yet.
    clearable: {
      type: Boolean,
      default: true,
    },
    assignment: {
      type: Object,
      required: true,
    },
    // Published-without-subscription rows can be texted one laborer at a time.
    textable: {
      type: Boolean,
      default: false,
    },
    // { icon, chipColor, textColor } from sentStatusDisplays: renders the
    // status as a tinted chip. Without it the status is plain text.
    statusDisplay: {
      type: Object,
      default: null,
    },
    // The published list drops its Text column when the modal itself texts.
    showTextColumn: {
      type: Boolean,
      default: true,
    },
    // Tooltip on the status chip, when the status needs explaining.
    statusWarning: {
      type: String,
      default: null,
    },
    // This row's laborer has a text in flight.
    texting: {
      type: Boolean,
      default: false,
    },
    // Another row action or a publish round is running.
    actionsDisabled: {
      type: Boolean,
      default: false,
    },
    clearing: {
      type: Boolean,
      default: false,
    },
    skipping: {
      type: Boolean,
      default: false,
    },
    // Row label: a plain number, or "4-A" / "4-B" for a multi-shift laborer.
    index: {
      type: [Number, String],
      default: 0,
    },
    // Width of the label column in status rows; the list sizes it to its
    // longest label so names line up without spare room.
    indexWidth: {
      type: String,
      default: '3ch',
    },
    isSelected: {
      type: Boolean,
    },
    selectable: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      isConfirming: false,
    }
  },
  computed: {
    hasLastColumn() {
      return this.skippable || this.showTextColumn
    }, // hasLastColumn
    columnStyles() {
      return statusRowColumnStyles(this.hasLastColumn)
    }, // columnStyles
    canText() {
      return !!this.assignment.phone && !this.actionsDisabled && !this.texting
    }, // canText
    rowNote() {
      return this.texting ? null : _get(this.assignment, 'rowText.message')
    }, // rowNote
    rowNoteClass() {
      return _get(this.assignment, 'rowText.result') ===
        rowTextResults.STATUS_CHANGED
        ? 'grey--text text--darken-2'
        : 'error--text'
    }, // rowNoteClass
    shiftSummary() {
      let parts = [`Job ${this.assignment.jobNumber || '—'}`]
      parts.push(this.assignment.startTime || 'No start time')
      return parts.join(' · ')
    }, // shiftSummary
    resultLabel() {
      return resultLabels[this.assignment.result] || null
    }, // resultLabel
    resultTextClass() {
      return this.assignment.result === publishResults.FAILED
        ? 'error--text'
        : 'success--text'
    }, // resultTextClass
    statusTextClass() {
      return this.skippable ? 'error--text' : 'grey--text text--darken-2'
    }, // statusTextClass
  },
  methods: {
    confirmText() {
      this.isConfirming = false
      this.$emit('text')
    }, // confirmText
    toggleSelection() {
      this.$emit('toggle-selection', this.assignment.shiftId)
    },
  },
}
</script>

<style lang="scss">
.text-assignment-item {
  // Status rows share one compact height whether or not they hold action
  // buttons, and the chip sits inside the text line instead of stretching it.
  &--status > .layout {
    // 5px above and below the 20px buttons keeps stacked circles apart.
    min-height: 30px;
    .v-menu,
    .v-menu__activator {
      vertical-align: top;
      height: 20px;
    }
    .v-btn--icon.v-btn--small {
      width: 20px;
      height: 20px;
      // The column centres the button; side margins would make it overflow
      // the 6% action column on a narrow screen.
      margin: 0;
      // Keeps the button from stretching its line box past the row height.
      vertical-align: top;
      // Scaled with the button so the icon keeps its breathing room. VIcon's
      // `small` sets the size inline, hence the override.
      .v-icon {
        font-size: 12px !important;
      }
    }
  }
  .status-chip.v-chip {
    // Optically centres the chip on the row's text.
    top: -1px;
    height: 18px !important;
    vertical-align: middle;
    .v-chip__content {
      height: 18px;
      padding: 0 8px 0 6px;
      font-size: 11px;
      line-height: 18px;
    }
    .v-icon.v-icon--left {
      margin: 0 4px 0 0;
      font-size: 13px !important;
    }
  }
  .night-shift {
    color: $blue;
    .night-shift-icon {
      font-size: 14px;
      color: $blue;
      vertical-align: -2px;
    }
  }
}
</style>
